import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { DotsIcon } from './icons'
import './MoreMenu.css'

export interface MoreMenuItem {
  label: string
  icon?: ReactNode
  onSelect: () => void
  danger?: boolean
  /** Bu öğenin üstüne ince bir çizgi: silme gibi işlemleri ötekilerden ayırır. */
  divided?: boolean
}

/**
 * "⋯" düğmesi ve altında açılan küçük menü (Masa Yönetimi, 22 Eki 2026).
 *
 * Her kartta kalem ve çöp kutusu yan yana durunca yirmi masa kırk simge
 * demekti; işlemler bu menüye toplandı. Dışarı basınca ve Esc ile kapanıyor.
 */
export function MoreMenu({
  items,
  label,
  align = 'end',
  className = '',
}: {
  items: MoreMenuItem[]
  /** Ekran okuyucu ve ipucu için: "Masa 3 işlemleri" gibi. */
  label: string
  /** Menü düğmenin hangi kenarına hizalansın; sağdaki kartta 'end'. */
  align?: 'start' | 'end'
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<CSSProperties>({ visibility: 'hidden' })
  const box = useRef<HTMLDivElement>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)

  // Sayfanın en üstüne (body) taşınıp ekrana göre konumlanıyor: kart
  // ızgarasını saran kutu taşanı kırpıyordu, üzerine gelince kalkan kart
  // (transform) da menüyü sonraki kartların altına itiyordu. Kenara
  // sığmazsa içeri kayıyor.
  useLayoutEffect(() => {
    if (!open || !btn.current || !menu.current) return
    const b = btn.current.getBoundingClientRect()
    const w = menu.current.offsetWidth
    const h = menu.current.offsetHeight
    const pad = 8
    let left = align === 'end' ? b.right - w : b.left
    left = Math.min(Math.max(left, pad), window.innerWidth - w - pad)
    // Altta yer yoksa düğmenin üstünde açılsın.
    const below = b.bottom + 4
    const top = below + h > window.innerHeight - pad ? Math.max(pad, b.top - 4 - h) : below
    setPos({ left, top })
  }, [open, align])

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (!box.current?.contains(t) && !menu.current?.contains(t)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    // Kaydırınca düğmeden kopmasın diye kapanıyor.
    const onMove = () => setOpen(false)
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open])

  return (
    <div ref={box} className={`more ${open ? 'is-open' : ''} ${className}`}>
      <button
        ref={btn}
        type="button"
        className="more__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={(e) => {
          // Kartın kendisine basmak başka bir iş yapıyorsa onu tetiklemesin.
          e.stopPropagation()
          setPos({ visibility: 'hidden' })
          setOpen((o) => !o)
        }}
      >
        <DotsIcon />
      </button>
      {open &&
        createPortal(
          <div ref={menu} role="menu" className="more__menu" style={pos}>
            {items.map((it) => (
              <div key={it.label} className={it.divided ? 'more__sep' : undefined}>
                <button
                  type="button"
                  role="menuitem"
                  className={`more__item ${it.danger ? 'is-danger' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpen(false)
                    it.onSelect()
                  }}
                >
                  {it.icon}
                  {it.label}
                </button>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}
